import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import derivativeService from "@/services/investments/securities/derivatives/DerivativeService.ts";

export function useFetchDerivativeSecurities() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['derivative_securities', user?.id],
    queryFn: async () => {
      return await derivativeService.fetchDerivativeSecurities(user.id);
    },
    enabled: !!user,
  });
}

export function useCreateDerivativeSecurity() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (security: Omit<DerivativeSecurityCreateRequest, "userId">) => {
      return await derivativeService.createDerivativeSecurity({...security, userId: user.id});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['derivative_securities'] }),
  });
}

export function useUpdateDerivativeSecurity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updateReq: DerivativeSecurityUpdateRequest) => {
      return await derivativeService.updateDerivativeSecurity(updateReq);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['derivative_securities'] });
      qc.invalidateQueries({ queryKey: ['derivative_security_detail'] });
    },
  });
}

export function useDeleteDerivativeSecurity() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      return await derivativeService.deleteDerivativeSecurity({ id, userId: user.id});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['derivative_securities'] }),
  });
}

export function useDerivativeSecurityDetail(id: string, currentUnderlyingPrice?: number) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['derivative_security_detail', id, user?.id, currentUnderlyingPrice],
    queryFn: async () => {
      return await derivativeService.fetchDerivativeSecurityById({
        id,
        userId: user.id,
        currentUnderlyingPrice,
      });
    },
    enabled: !!user && !!id,
  });
}
